import { LEAVE_DASHBOARD } from '../../dashboard/Dashboard.actions';
import { getApplicantFirstName } from '../../../api/lendingAPI/CustomerStateAPIHelper';
import {
    DASHBOARD_ERROR,
    DASHBOARD_READY,
    UPDATE_PLANS,
    SELECT_NEW_PLAN
} from './Dashboard.actions';
import {
    getApplicationState,
    getCreditLimit,
    getEndedPayments,
    getOngoingPaymentPlans,
    getPaymentPlans,
    getCapturedPlans,
    getCancelledPlans,
    getIwocaPayApplication,
    getTotalScheduledPayments
} from './Dashboard.helpers';
import {
    Application,
    CustomerState,
    PayLinksResponse,
    EndedPaymentPlan,
    ApplicationState,
    OngoingPaymentPlan,
    Plan,
    CapturedPayment,
    CapturedPaymentsResponse,
    CancelledPlan,
    Accounts
} from './Dashboard.types';
import { REGISTER_DEBIT_CARD_SUCCESS } from '../../modals/addPaymentMethodModal/AddPaymentMethodModal.actions';

const initialState = {
    ready: false,
    error: false,
    drawdownLoading: false,
    creditLimit: null,
    buyerFirstName: '',
    application: null,
    accounts: null,
    payLinks: null,
    capturedPlans: [],
    endedPayments: [],
    cancelledPayments: [],
    plans: [],
    ongoingPaymentPlans: [],
    selectedPlan: null,
    iwocaPayApplication: null,
    rawApplication: null,
    rawPayLinks: [],
    rawCapturedPayments: []
};

type IwocaPayDashboardState = {
    ready: boolean;
    error: boolean;
    drawdownLoading: boolean;
    buyerFirstName: string;
    capturedPlans: CapturedPayment[];
    creditLimit: number | null;
    iwocaPayApplication: Application | null;
    rawApplication: Application | null;
    application: ApplicationState;
    accounts: object | null;
    endedPayments: EndedPaymentPlan[];
    cancelledPayments: CancelledPlan[];
    ongoingPaymentPlans: OngoingPaymentPlan[];
    plans: Plan[];
    selectedPlan: Plan | null;
    rawPayLinks: PayLinksResponse;
    rawCapturedPayments: CapturedPaymentsResponse;
};

export type IwocaPayDashboardReadyAction = {
    type: typeof DASHBOARD_READY;
    application: Application;
    customer_state: CustomerState;
    payLinks: PayLinksResponse;
    capturedPayments: CapturedPaymentsResponse;
    accounts: Accounts;
};

type IwocaPayDashboardAction =
    | IwocaPayDashboardReadyAction
    | {
          type: typeof DASHBOARD_ERROR;
      }
    | {
          type: typeof LEAVE_DASHBOARD;
      }
    | { type: typeof REGISTER_DEBIT_CARD_SUCCESS }
    | { type: typeof SELECT_NEW_PLAN; plan: Plan }
    | {
          type: typeof UPDATE_PLANS;
          accounts: Accounts;
          application: Application;
      };

// eslint-disable-next-line import/no-default-export
export default function (
    state: IwocaPayDashboardState = initialState,
    action: IwocaPayDashboardAction
): IwocaPayDashboardState {
    switch (action.type) {
        case DASHBOARD_READY: {
            const {
                accounts,
                application,
                customer_state,
                payLinks,
                capturedPayments
            } = action;

            const totalScheduledAmount = getTotalScheduledPayments(
                payLinks,
                capturedPayments
            );

            const capturedPlans = getCapturedPlans(capturedPayments, payLinks);
            const paymentPlans = getPaymentPlans(accounts, payLinks);
            const applicationState = getApplicationState({
                accounts,
                payLinks,
                application,
                totalScheduledAmount,
                capturedPayments
            });

            const ongoingPaymentPlans = getOngoingPaymentPlans(paymentPlans);
            const cancelledPayments = getCancelledPlans(
                capturedPayments,
                payLinks
            );

            return {
                ...state,
                buyerFirstName: getApplicantFirstName(customer_state),
                rawApplication: application,
                iwocaPayApplication: getIwocaPayApplication(application),
                application: applicationState,
                plans: [
                    ...paymentPlans,
                    ...capturedPlans,
                    ...cancelledPayments,
                    applicationState
                ],
                selectedPlan: applicationState ? applicationState : null,
                creditLimit: getCreditLimit(accounts, totalScheduledAmount),
                capturedPlans,
                ongoingPaymentPlans,
                endedPayments: getEndedPayments(accounts, payLinks),
                cancelledPayments,
                ready: true,
                rawPayLinks: payLinks,
                rawCapturedPayments: capturedPayments
            };
        }
        case SELECT_NEW_PLAN:
            const { plan } = action;

            return {
                ...state,
                selectedPlan: plan
            };
        case DASHBOARD_ERROR:
            return {
                ...state,
                error: true
            };
        case LEAVE_DASHBOARD:
            return initialState;
        default:
            return state;
        case UPDATE_PLANS: {
            const { accounts, application } = action;
            const { rawPayLinks, rawCapturedPayments, plans } = state;
            const totalScheduledAmount = getTotalScheduledPayments(
                rawPayLinks,
                rawCapturedPayments
            );

            if (!application) {
                return { ...state };
            }

            const applicationState = getApplicationState({
                accounts,
                payLinks: rawPayLinks,
                application: application,
                totalScheduledAmount,
                capturedPayments: rawCapturedPayments
            });

            return {
                ...state,
                application: applicationState,
                plans: [...plans, applicationState],
                selectedPlan: applicationState ? applicationState : null,
                rawApplication: application,
                iwocaPayApplication: getIwocaPayApplication(application)
            };
        }
    }
}
